import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DOMAIN_TRANSFER_STAGE_LABELS } from "@hybrid-escrow/shared";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { CountdownTimer } from "../../components/CountdownTimer";
import { formatDateTime } from "../../lib/format";

const STAGE_TONE: Record<string, string> = {
  AWAITING_SELLER_CODE: "badge--warning",
  READY_FOR_BUYER: "badge--success",
  CODE_REVEALED: "badge--neutral",
};

/** Read-only, decrypted-on-demand view of what the seller submitted — for dispute support only.
 *  Unlike account transfers, there's no admin action to take here (see
 *  services/domainTransfers.ts for why): submission auto-advances the order and the buyer's
 *  reveal is entirely self-service. */
function TransferCodeViewer({ orderId }: { orderId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...queryKeys.adminDomainTransfers, orderId, "credential"],
    queryFn: () => adminApiClient.getDomainTransferCredential(orderId),
  });

  if (isLoading) return <p className="text-sm muted">Loading what the seller submitted…</p>;
  if (isError) return <p className="text-sm muted">{describeError(error)}</p>;
  if (!data) return null;

  return (
    <div className="card stack" style={{ background: "var(--color-bg-subtle, transparent)" }}>
      <p className="text-sm muted" style={{ margin: 0 }}>
        Submitted by the seller {formatDateTime(data.submittedAt)}
        {data.revealedAt && ` · revealed by the buyer ${formatDateTime(data.revealedAt)}`}
      </p>
      <div className="row row--between">
        <span className="muted text-sm">Transfer / EPP code</span>
        <span className="mono">{data.transferCode}</span>
      </div>
      {data.notes && (
        <div className="stack" style={{ gap: "var(--space-1)" }}>
          <span className="muted text-sm">Notes</span>
          <p className="text-sm" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {data.notes}
          </p>
        </div>
      )}
    </div>
  );
}

export function AdminDomainTransfersPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminDomainTransfers,
    queryFn: () => adminApiClient.listDomainTransfers({ limit: 100 }),
  });

  return (
    <div className="stack">
      <div>
        <h1>Domain transfers</h1>
        <p className="muted text-sm">
          No admin action is normally needed here — the seller's transfer code submission
          auto-advances the order, and the buyer reveals it themselves. This page exists for
          dispute support: expand an order to see exactly what was submitted and when.
        </p>
      </div>

      {isLoading && <LoadingState label="Loading domain transfers…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}

      {data && data.items.length === 0 && <p className="muted">No domain-transfer orders yet.</p>}

      {data &&
        data.items.map((item) => (
          <div key={item.id} className="card stack">
            <div className="row row--between">
              <div>
                <strong>{item.listingTitle}</strong>
                <p className="text-sm muted" style={{ margin: 0 }}>
                  {item.domainName ?? "—"} · Seller: {item.sellerUsername ?? "—"} · Buyer:{" "}
                  {item.buyerUsername ?? "—"} · Opened {formatDateTime(item.createdAt)}
                </p>
              </div>
              <div className="row" style={{ gap: "0.5rem" }}>
                <span className="badge badge--neutral">{item.financialStatus}</span>
                <span className={`badge ${STAGE_TONE[item.domainTransferStage] ?? "badge--neutral"}`}>
                  {DOMAIN_TRANSFER_STAGE_LABELS[
                    item.domainTransferStage as keyof typeof DOMAIN_TRANSFER_STAGE_LABELS
                  ] ?? item.domainTransferStage}
                </span>
                {item.domainTransferStage !== "CODE_REVEALED" && item.disputeDeadline && (
                  <CountdownTimer deadlineIso={item.disputeDeadline} />
                )}
              </div>
            </div>

            {item.domainTransferStage === "AWAITING_SELLER_CODE" ? (
              <p className="text-sm muted">Waiting on the seller — nothing to view yet.</p>
            ) : (
              <div className="row row--between">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  {expandedId === item.id ? "Close" : "View"}
                </button>
              </div>
            )}

            {expandedId === item.id && item.domainTransferStage !== "AWAITING_SELLER_CODE" && (
              <TransferCodeViewer orderId={item.id} />
            )}
          </div>
        ))}
    </div>
  );
}
